<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="open" class="fixed inset-0 z-[9999]">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50" />

        <!-- Fullscreen click-catcher -->
        <div
          class="absolute inset-0 flex items-start justify-center p-4 sm:p-6"
          @click.self="close"
        >
          <!-- Modal -->
          <div
            class="mt-16 w-full max-w-2xl rounded-2xl border border-[var(--color-mode-border)] bg-[var(--sidebar-background)] shadow-2xl overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Zoek chats"
          >
            <!-- Search bar -->
            <div
              class="flex items-center gap-3 px-4 py-3 border-b border-[var(--color-mode-border)]"
            >
              <UiIcon name="custom-icons:search" size="md" class="opacity-70" />

              <input
                ref="inputEl"
                v-model="query"
                type="text"
                class="w-full bg-transparent outline-none text-[var(--color-mode-text)] placeholder:opacity-60"
                placeholder="Zoek chats..."
                role="combobox"
                aria-expanded="true"
                aria-controls="search-results"
                :aria-activedescendant="
                  selectedId ? `chat-opt-${selectedId}` : undefined
                "
              />

              <button
                class="text-xs opacity-70 hover:opacity-100 px-2 py-1 rounded-lg border border-[var(--color-mode-border)]"
                @click="close"
                type="button"
              >
                Esc
              </button>
            </div>

            <!-- Results -->
            <div
              class="max-h-[60vh] overflow-y-auto custom-scrollbar search-scrollbar"
              id="search-results"
            >
              <div
                v-if="flatFiltered.length === 0"
                class="px-4 py-6 opacity-70"
              >
                Geen chats gevonden.
              </div>

              <div v-else>
                <div v-for="(chats, label) in groupedChats" :key="label">
                  <div
                    class="px-4 pt-4 pb-2 text-xs uppercase tracking-wide opacity-60"
                  >
                    {{ label }}
                  </div>

                  <ul role="listbox" aria-label="Chat resultaten">
                    <li
                      v-for="chat in chats"
                      :key="chat.id"
                      role="option"
                      :id="`chat-opt-${chat.id}`"
                      :aria-selected="chat.id === selectedId"
                    >
                      <button
                        type="button"
                        class="w-full text-left px-4 py-3 flex items-center justify-between gap-4 hover:bg-black/5 dark:hover:bg-white/5"
                        :class="
                          chat.id === selectedId
                            ? 'bg-black/5 dark:bg-white/5'
                            : ''
                        "
                        @mouseenter="selectedId = chat.id"
                        @click="goToChat(chat)"
                      >
                        <div class="min-w-0">
                          <div class="truncate font-medium">
                            {{ chat.title || "Nieuwe chat" }}
                          </div>
                        </div>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Footer hint -->
            <div
              class="px-4 py-2 text-xs opacity-60 border-t border-[var(--color-mode-border)] flex items-center justify-between"
            >
              <span>↑ ↓ navigeren</span>
              <span>Enter openen</span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
const router = useRouter();

type ChatItem = {
  id: string;
  link?: string;
  title?: string;
  name?: string;
  preview?: string;
  created_at?: string; // ISO string
};

const props = defineProps<{
  open: boolean;
  chats: ChatItem[];
}>();

const emit = defineEmits<{ (e: "close"): void }>();

const query = ref("");
const inputEl = ref<HTMLInputElement | null>(null);
const selectedId = ref<string | null>(null);

const close = () => emit("close");

const normalize = (v: unknown) =>
  String(v ?? "")
    .toLowerCase()
    .trim();

const getTitle = (chat: ChatItem) =>
  chat.title || chat.name || chat.preview || "Nieuwe chat";

const flatFiltered = computed(() => {
  const q = normalize(query.value);
  if (!q) return props.chats;

  return props.chats.filter((c) => {
    const hay = normalize(getTitle(c)) + " " + normalize(c.preview);
    return hay.includes(q);
  });
});

function move(delta: number) {
  const list = flatFiltered.value;
  if (!list.length) return;

  const idx = Math.max(
    0,
    list.findIndex((c) => c.id === selectedId.value)
  );
  const next = (idx + delta + list.length) % list.length;
  selectedId.value = list[next].id;

  // optioneel: scroll into view (kan later)
}

function openSelected() {
  const chat = flatFiltered.value.find((c) => c.id === selectedId.value);
  if (chat) goToChat(chat);
}

function goToChat(chat: ChatItem) {
  const href = chat.link || `/chat/${chat.id}`;
  close();
  router.push(href);
}

// Today/Yesterday/weekday/date labels (jij had deze al)
const relativeDateLabel = (iso?: string) => {
  if (!iso) return "Onbekend";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Onbekend";

  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const startOfDate = new Date(date);
  startOfDate.setHours(0, 0, 0, 0);

  const diffDays =
    (startOfToday.getTime() - startOfDate.getTime()) / (1000 * 60 * 60 * 24);

  if (diffDays === 0) return "Vandaag";
  if (diffDays === 1) return "Gisteren";
  if (diffDays < 7)
    return date.toLocaleDateString("nl-NL", { weekday: "long" });

  return date.toLocaleDateString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const groupedChats = computed(() => {
  const groups: Record<string, ChatItem[]> = {};

  flatFiltered.value.forEach((chat) => {
    const label = relativeDateLabel(chat.created_at);
    (groups[label] ||= []).push(chat);
  });

  return groups;
});

// Focus + init selection
watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      query.value = "";
      selectedId.value = flatFiltered.value[0]?.id ?? null;
      await nextTick();
      inputEl.value?.focus();
    }
  }
);

// Als filter wijzigt: clamp selectedId
watch(
  () => flatFiltered.value,
  (list) => {
    if (!list.length) {
      selectedId.value = null;
      return;
    }
    if (!selectedId.value || !list.some((c) => c.id === selectedId.value)) {
      selectedId.value = list[0].id;
    }
  },
  { deep: true }
);

// Global key handling while open (Esc / arrows / enter)
function onKeydown(e: KeyboardEvent) {
  if (!props.open) return;

  if (e.key === "Escape") {
    e.preventDefault();
    close();
    return;
  }

  if (e.key === "ArrowDown") {
    e.preventDefault();
    move(1);
    return;
  }

  if (e.key === "ArrowUp") {
    e.preventDefault();
    move(-1);
    return;
  }

  if (e.key === "Enter") {
    e.preventDefault();
    openSelected();
    return;
  }
}

onMounted(() => window.addEventListener("keydown", onKeydown));
onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));

// Prevent body scroll when open (jij had dit al)
watch(
  () => props.open,
  (isOpen) => {
    document.body.style.overflow = isOpen ? "hidden" : "";
  }
);

onBeforeUnmount(() => {
  document.body.style.overflow = "";
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.12s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* === Scrollbar fix voor dark mode modal === */

/* Firefox */
.search-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(78, 78, 78, 0.9) transparent;
}

/* WebKit (Chrome, Edge, Safari) */
.search-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.search-scrollbar::-webkit-scrollbar-track {
  background: transparent; /* <-- BELANGRIJK */
}

.search-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(78, 78, 78, 0.8);
  border-radius: 9999px;
  border: 2px solid transparent;
  background-clip: content-box;
}

.search-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(120, 120, 120, 0.9);
}

/* Light mode (optioneel, maar netjes) */
.light .search-scrollbar {
  scrollbar-color: rgba(205, 205, 205, 0.9) transparent;
}

.light .search-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(205, 205, 205, 0.9);
}
</style>
